import type { CampaignStatus } from "./managedMarketingApi";

export const statusLabels: Record<CampaignStatus, string> = {
  requested: "Request received",
  preparing: "Preparing creative",
  awaiting_approval: "Your approval needed",
  approved: "Approved for launch",
  live: "Live",
  pause_requested: "Pause requested",
  paused: "Paused",
  completed: "Completed",
  rejected: "Not approved",
};
export const money = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    cents / 100,
  );
export const dateLabel = (date: string) =>
  new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export function dollarsToCents(value: string): number {
  if (!/^\d+(\.\d{1,2})?$/.test(value.trim()))
    throw new Error(
      "Enter a dollar amount with no more than two decimal places.",
    );
  const [dollars, fraction = ""] = value.trim().split(".");
  const cents = Number(dollars) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents <= 0)
    throw new Error("Enter an amount greater than $0.");
  return cents;
}

export function errorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  if (Array.isArray(response)) return response.join(" ");
  if (typeof response === "string") return response;
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export function safeWebUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

export function activityText(event: {
  type: string;
  data: Record<string, unknown>;
}): string {
  if (typeof event.data.message === "string") return event.data.message;
  if (typeof event.data.note === "string" && event.data.note)
    return event.data.note;
  const labels: Record<string, string> = {
    campaign_requested: "Campaign request received",
    requested: "Campaign request received",
    creative_updated: "A new creative version is ready for review",
    creative_approved: "Creative approved by the producer",
    change_requested: "The producer requested a change",
    pause_requested: "Pause requested by the producer",
    funding_confirmed: "Campaign payment confirmed",
    refund_requested: "Refund submitted for processing",
    refund_confirmed: "Refund confirmed",
    refund_failed: "Refund could not be completed",
    lead_delivered: "A campaign lead was delivered to the CRM",
    spend_recorded: "Ad spend recorded",
    campaign_updated: "Campaign details updated",
    operator_update: "Campaign updated by the team",
    approve: "Creative approved by the producer",
    pause: "Campaign pause requested",
    change_request: "The producer requested a change",
  };
  const amount =
    typeof event.data.amountCents === "number"
      ? ` · ${money(event.data.amountCents)}`
      : "";
  const status =
    typeof event.data.status === "string" && event.data.status in statusLabels
      ? ` · ${statusLabels[event.data.status as CampaignStatus]}`
      : "";
  return `${labels[event.type] || event.type.replaceAll("_", " ")}${amount}${status}`;
}
